import { useEffect, useRef, useState, type ReactNode } from "react";
import { Dialog, Hint } from "@shared/ui/bits";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import {
  IconBack,
  IconMembers,
  IconPinned,
  IconTextChannel,
  IconThreads,
  IconVoiceChannel,
  IconCall,
  IconForumChannel,
  IconAnnouncementChannel,
  IconFollow,
} from "@shared/ui/icons";
import { overlaid } from "@shared/ui/hotkey";

export type Side = "pins" | "threads" | "members" | "search";

type Pop = "threads" | "pins";

/**
 * Шапка канала — как в Discord: одна панель с именем канала и темой слева,
 * значками справа и полем поиска в конце. На широком экране она тянется над
 * лентой и правой колонкой, колонка начинается под ней.
 *
 * Значки — как в Discord: ветки и закреплённые открываются окном под своим
 * значком, участники — колонкой справа. Без подписей и без движения при
 * прокрутке: прежняя шапка каплями сжималась, когда ленту листали вверх, и это
 * мешало читать.
 *
 * Поиск — полем в конце (`search`, `SearchField`): меню фильтров под ним,
 * находки — колонкой справа.
 */
export function ChatHead({
  title,
  topic,
  isDirect,
  side,
  hasMembers,
  threadUnread,
  onSide,
  onBack,
  onCall,
  callHint = "Вернуться к звонку",
  callStatus = null,
  isForum = false,
  isAnnouncement = false,
  onFollow,
  threads,
  pins,
  search,
}: {
  title: string;
  topic: string | null;
  isDirect: boolean;
  side: Side | null;
  hasMembers: boolean;
  threadUnread: number;
  onSide: (next: Side) => void;
  onBack: () => void;
  /** Чат голосового канала: вернуться к звонку. Есть — значит, канал голосовой. */
  onCall?: () => void;
  /** Подпись кнопки звонка: у переписки звонок начинают, у голосового канала к нему возвращаются. */
  callHint?: string;
  /** «Участвует в звонке» — у переписки, где идёт звонок, как в Discord. */
  callStatus?: string | null;
  /** Форум — свой значок вместо решётки. */
  isForum?: boolean;
  /** Канал объявлений — мегафон вместо решётки. */
  isAnnouncement?: boolean;
  /** «Подписаться» на канал объявлений — как кнопка «Follow» в Discord. */
  onFollow?: () => void;
  /** Окно веток под значком; `close` — закрыть окно (выбрали ветку). */
  threads?: (close: () => void) => ReactNode;
  /** Окно закреплённых под значком. */
  pins?: (close: () => void) => ReactNode;
  /** Поле поиска в конце шапки. */
  search?: ReactNode;
}) {
  /** Тема целиком — по щелчку, как в Discord: в шапке она обрезана. */
  const [topicOpen, setTopicOpen] = useState(false);
  const [pop, setPop] = useState<Pop | null>(null);

  /*
   * Ctrl+P — закреплённые, Ctrl+U — участники, Ctrl+' — звонок в переписке,
   * как в Discord. Обработчик читает свежие свойства через ссылку: слушатель
   * на окне ставится один раз.
   */
  const keys = useRef<(event: KeyboardEvent) => boolean>(() => false);
  keys.current = (event) => {
    if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return false;
    // Над окнами молчим — кроме собственного окна закреплённых: Ctrl+P его и закрывает.
    const own = (event.target as HTMLElement | null)?.closest?.(".head-pop");
    if (!own && overlaid(event)) return false;
    if (event.code === "KeyP" && pins) setPop((open) => (open === "pins" ? null : "pins"));
    else if (event.code === "KeyU" && !isDirect && hasMembers) onSide("members");
    else if (event.code === "Quote" && isDirect && onCall) onCall();
    else return false;
    return true;
  };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (keys.current(event)) event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const close = () => setPop(null);

  /** Значок с окном под ним — ветки и закреплённые. */
  const popover = (key: Pop, label: string, icon: ReactNode, content: (close: () => void) => ReactNode, badge = 0) => (
    <Popover open={pop === key} onOpenChange={(next) => setPop(next ? key : null)}>
      <Hint side="bottom" label={label}>
        <PopoverTrigger asChild>
          <button type="button" aria-label={label} className={`chat-head-tool ${pop === key ? "on" : ""}`}>
            {icon}
            {badge > 0 && <span className="badge thread">{badge}</span>}
          </button>
        </PopoverTrigger>
      </Hint>
      <PopoverContent align="end" sideOffset={12} onOpenAutoFocus={(event) => event.preventDefault()}>
        {/* Выбрали — окно закрывается: ветка открывается справа, к закреплённому переходит лента. */}
        <div
          className="head-pop glass-host"
          onClickCapture={(event) => {
            if ((event.target as HTMLElement).closest(".thread-row, .pin-go")) window.setTimeout(close);
          }}
        >
          {content(close)}
        </div>
      </PopoverContent>
    </Popover>
  );

  return (
    <header className="main-head chat-head glass-host">
      <button type="button" className="icon-btn mobile-only" onClick={onBack} title="Назад">
        <IconBack size={15} />
      </button>

      <span className="chat-head-title glass-host">
        {!isDirect &&
          (onCall ? (
            <IconVoiceChannel size={18} className="head-title-icon" />
          ) : isForum ? (
            <IconForumChannel size={18} className="head-title-icon" />
          ) : isAnnouncement ? (
            <IconAnnouncementChannel size={18} className="head-title-icon" />
          ) : (
            <IconTextChannel size={18} className="head-title-icon" />
          ))}
        <span className="head-title-name">{title}</span>
        {callStatus && (
          <span className="head-call">
            <IconCall size={13} /> {callStatus}
          </span>
        )}
        {topic && (
          <>
            <span className="head-title-sep" aria-hidden />
            <button type="button" className="head-title-topic" title="Тема канала целиком" onClick={() => setTopicOpen(true)}>
              {topic}
            </button>
          </>
        )}
      </span>

      {/* Значки и поиск — на одной подложке справа, имя канала — на своей слева. */}
      <span className="chat-head-side glass-host">
        <span className="chat-head-tools" role="toolbar" aria-label="Правая колонка">
          {onCall && (
            <Hint side="bottom" label={callHint}>
              <button type="button" className="chat-head-tool" aria-label={callHint} onClick={onCall}>
                {isDirect ? <IconCall size={18} /> : <IconVoiceChannel size={18} />}
              </button>
            </Hint>
          )}
          {onFollow && (
            <Hint side="bottom" label="Подписаться: получать объявления в своём канале">
              <button type="button" className="chat-head-tool" aria-label="Подписаться" onClick={onFollow}>
                <IconFollow size={18} />
              </button>
            </Hint>
          )}
          {threads && popover("threads", "Ветки", <IconThreads size={18} />, threads, threadUnread)}
          {pins && popover("pins", "Закреплённые сообщения", <IconPinned size={18} />, pins)}
          {!isDirect && hasMembers && (
            <Hint side="bottom" label={side === "members" ? "Скрыть участников" : "Участники"}>
              <button
                type="button"
                aria-pressed={side === "members"}
                aria-label="Участники"
                className={`chat-head-tool ${side === "members" ? "on" : ""}`}
                onClick={() => onSide("members")}
              >
                <IconMembers size={18} />
              </button>
            </Hint>
          )}
        </span>

        {search}
      </span>

      {topicOpen && topic && (
        <Dialog title={`#${title}`} onClose={() => setTopicOpen(false)}>
          <p className="head-topic-full">{topic}</p>
        </Dialog>
      )}
    </header>
  );
}
